/**
 * Smart chat-list preview truncation.
 * Rules (matches user expectation from Session 12 follow-up):
 *   • Max 3 emojis / picto-graphemes, then '…'
 *   • Plain text: max 5 words OR 25 chars, then '…'
 *   • Total hard cap: 25 grapheme clusters
 * Uses Intl.Segmenter for emoji-safe grapheme counting when available.
 */

const EMOJI_RE = /\p{Extended_Pictographic}/u;

const getGraphemes = (str) => {
  if (!str) return [];
  try {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      const seg = new Intl.Segmenter('en', { granularity: 'grapheme' });
      return Array.from(seg.segment(str), s => s.segment);
    }
  } catch (_) {}
  // Fallback: array spread splits by code points (good enough for BMP + astral)
  return Array.from(str);
};

export const truncatePreview = (raw) => {
  if (typeof raw !== 'string' || !raw) return raw || '';
  const graphemes = getGraphemes(raw);
  const hasEmoji = graphemes.some(g => EMOJI_RE.test(g));

  if (hasEmoji) {
    // Emoji-mixed: allow up to 3 emojis + text prefix, cap overall at 25 clusters
    let out = '';
    let emojiCount = 0;
    for (let i = 0; i < graphemes.length; i++) {
      const g = graphemes[i];
      if (EMOJI_RE.test(g)) {
        if (emojiCount >= 3) return out + '…';
        emojiCount += 1;
      }
      out += g;
      if (i >= 24) {
        return out + (i < graphemes.length - 1 ? '…' : '');
      }
    }
    return out;
  }

  // Plain text: cap at 5 words OR 25 chars
  const words = raw.trim().split(/\s+/);
  if (words.length > 5) return words.slice(0, 5).join(' ') + '…';
  if (raw.length > 25) return raw.slice(0, 25) + '…';
  return raw;
};

export default truncatePreview;

/**
 * Turn a chat.last_message (which may be a formatted STRING from /chat/rooms
 * or a raw MongoDB message OBJECT from /handler/chats) into a preview string
 * for the chat list. Handles sticker / voice / photo / document / call.
 */
export const formatLastMessagePreview = (lm, fallback = 'No messages yet') => {
  if (!lm) return fallback;
  if (typeof lm === 'string') return lm || fallback;
  // Object form (from /handler/chats)
  if (lm.is_sticker) return '🎨 Sticker';
  if (lm.voice_url) return '🎤 Voice message';
  if (lm.image_url) return '📷 Photo';
  if (lm.document_url) return '📄 ' + (lm.document_name || 'Document');
  if (lm.call_info) return '📞 Voice call';
  return lm.text || fallback;
};

/**
 * Aggressive name truncation for chat-list rows on mobile.
 * Caps at ~14 chars OR 3 words — whichever is shorter — then '…'.
 * Short names ("VIRAT", "UJJ01") pass through unchanged.
 */
export const truncateName = (raw) => {
  if (typeof raw !== 'string' || !raw) return raw || '';
  const clean = raw.trim();
  const words = clean.split(/\s+/);
  if (words.length > 3) return words.slice(0, 3).join(' ') + '…';
  if (clean.length > 14) return clean.slice(0, 14) + '…';
  return clean;
};
